import {VTableComponent} from './table_v2/VTableComponent.js';
import {VizData} from './TableRows.js';
import {FilterCriteria} from './TableRows.js';
import {VisualizationChartComponent} from './chart/VisualizationChartComponent.js';
import ColumnPickerDialog from './ColumnPickerDialog.js';
import EditColumnDialog from "./EditColumnDialog.js";
import HttpError from "../../entities/error/HttpError.js";
import SimpleNotificationDialog from "../dialog/SimpleNotificationDialog.js";
import TableColumns, {ColumnValidationError} from "./TableColumns.js";
import TableColumn from "./TableColumn.js";
import LineConfig from "./LineConfig.js";
import {DataTable} from "./TableRows.js";
import {safeText} from "../../util/CommonUtils.js";
import MouseBlockingUnderlay from "../MouseBlockingUnderlay.js";
import OperationNotAllowedError from "../../entities/error/OperationNotAllowedError.js";

export class VisualisationComponent {
    /** @type {function} */
    #removeNewVisualizationClass;
    /** @type {function} */
    #onMouseLeave;
    /** @type {number} */
    #timer;

    /**
     * @param {DataTable} dataTable
     * @param {VisualizationDataSelection} selection
     * @param {TableColumns} defaultColumns
     * @param {VisualizationProperties} props
     * @param tableDataConverters
     * @param {VisualizationGrid} visGrid
     * @param {RegressionApiClient} regressionApiClient
     */
    constructor(dataTable, selection, defaultColumns, props,
                tableDataConverters, visGrid, regressionApiClient) {
        this._props = props;
        this._visGrid = visGrid;
        this._root = this._render(props.card.name);
        const filter = props.table.rowFilter ? new FilterCriteria(props.table.rowFilter) : null;
        this._tableVizData = new VizData(dataTable, {
            converters: tableDataConverters,
            filter
        });
        this.table = new VTableComponent(selection);
        this.table.render(this._root.querySelector('.table-tab'), this._tableVizData,
            {
                rowHeightPx: Math.max(24, parseInt(props.table.rowHeight)),
                tableHeightPx: this._root.getBoundingClientRect().height || 500
            });
        const chartVizData = new VizData(dataTable, {filter});
        this.chart = new VisualizationChartComponent(this._root.querySelector('.chart-tab'), chartVizData, selection, props, regressionApiClient);
        this.chart.onSave(async () => {
            await this._visGrid.saveProperties();
        });
        this.chart.onSelectionChanged((data)=>{
            if (typeof this._callbacks['selection-changed'] === "function")
                this._callbacks['selection-changed'](data)
        });
        this._grid = null;
        this._defaultColumns = defaultColumns;
        this._callbacks = {};

        const visOptionsDropdown = this._root.querySelector('.vis-options-dropdown');
        let mouseBlockingUnderlay;

        this._root.querySelector('svg#vis-more-icon').addEventListener('click', () => {
            visOptionsDropdown.querySelectorAll('li').forEach(li=>{
                li.style.display = null
            })
            if (this._props.card.activeView === 'table')
                visOptionsDropdown.querySelectorAll('li.item-separator, li[data-purpose="show options"]').forEach(li=>{
                    li.style.display = 'none'
                })
            if (this._props.card.activeView === 'chart')
                visOptionsDropdown.querySelector('li[data-purpose="export csv"]').style.display = 'none'
            mouseBlockingUnderlay = MouseBlockingUnderlay.show(() => {
                visOptionsDropdown.toggleAttribute('visible');
            });
            visOptionsDropdown.toggleAttribute('visible');
        })

        visOptionsDropdown.querySelectorAll('li').forEach(li => {
            li.addEventListener('click', async () => {
                visOptionsDropdown.toggleAttribute('visible');
                mouseBlockingUnderlay.remove();
                switch (li.dataset.purpose) {
                    case 'select columns' : {
                        this.showEditTableDialog(0);
                        break;
                    }
                    case 'add calculated' : {
                        this.showColumnPropertiesDialog(new TableColumn('', ''), -1);
                        break;
                    }
                    case 'filter rows' : {
                        this.showEditTableDialog(1);
                        break;
                    }
                    case 'show options' : {
                        this.chart.showStatisticOptionsDialog()
                        break;
                    }
                    case 'export csv' : {
                        let csvString = this._tableVizData.toCsv();
                        const link = document.createElement('a');
                        link.href = 'data:attachment/csv,' + encodeURIComponent(csvString);
                        link.target = '_blank';
                        link.download = props.card.name + '.csv';
                        this._root.appendChild(link);
                        link.click();
                        link.remove();
                        break;
                    }
                    case 'duplicate' : {
                        let properties = this._props.clone();
                        if (typeof this._callbacks['duplicate'] === "function")
                            this._callbacks['duplicate'](properties)
                        break;
                    }
                    case 'delete' : {
                        this._root.remove();
                        this.table.onDelete();
                        if (this._grid)
                            await this._grid.onVisRemoved(this)
                    }
                }
            })
        });

        this._root.querySelector('#toggle-view').addEventListener('click', async () => {
            const newViewMode = this._props.card.activeView === 'table' ? 'chart' : 'table';
            const isValidAxes = newViewMode === 'chart' ? this.chart.isValidAxes() : true;
            this.switchView(newViewMode);
            if (!isValidAxes && newViewMode === "chart") {
                this.#showAxesChoiceDialog()
            }
            this._visGrid.saveProperties();
        })

        let visName = this._root.querySelector('.vis-name edit-text');
        visName.addEventListener('change', async () => {
            this._props.card.name = visName.value.trim();
            await this._visGrid.saveProperties();
        })

        this._tableVizData.on('column-moved', async () => {
            this._props.table.columns = this._tableVizData.getColumns().toServerJson();
            await this._visGrid.saveProperties();
        });
        this.table.on('columnWidth-changed', async (_) => {
            this._props.table.columns = this._tableVizData.getColumns().toServerJson();
            await this._visGrid.saveProperties();
        });
        this.table.on('rowHeight-changed', async (rowHeightPx) => {
            this._props.table.rowHeight = rowHeightPx;
            await this._visGrid.saveProperties();
        });
        this.table.on('header-click', (columnIndex) => {
            const column = this._tableVizData.getColumns().getByIndex(columnIndex);
            this.showColumnPropertiesDialog(column, columnIndex);
        });
        this.table.on('selection-changed', (data) => {
            if (typeof this._callbacks['selection-changed'] === "function")
                this._callbacks['selection-changed'](data)
        });
    }

    onTableRowClick(callback) {
        this.table.onRowClick(callback);
    }

    /**
     * @param {number} rowIndex
     * @param {number} cellIndex
     */
    markCellAsSelected(rowIndex, cellIndex) {
        this.table.markCellAsSelected(rowIndex, cellIndex);
    }

    unselectActiveCell() {
        this.table.unselectActiveCell()
    }

    getRowIndexWithActiveCell() {
        return this.table.getRowIndexWithActiveCell();
    }

    on(eventName, callback) {
        this._callbacks[eventName] = callback;
    }

    /**
     * @param {TableColumn} tableColumn
     * @return {string[]}
     */
    addColumn(tableColumn) {
        this._tableVizData.addColumn(tableColumn);
        this._props.table.columns = this._tableVizData.getColumns().toServerJson();
    }

    /**
     * @param {number} index
     * @param {TableColumn} tableColumn
     * @return {string[]}
     */
    updateColumn(index, tableColumn) {
        this._tableVizData.updateColumn(index, tableColumn);
        this._props.table.columns = this._tableVizData.getColumns().toServerJson();
    }

    /**
     * @param {string} columnName
     */
    removeColumn(columnName) {
        const index = this._tableVizData.getColumns().getIndexedColumns([columnName]).indices[0];
        this._tableVizData.deleteColumn(index);
        this._props.table.columns = this._tableVizData.getColumns().toServerJson();
    }

    showEditTableDialog(activeTab = 0) {
        const defaultColumns = this._defaultColumns.getColumns();
        const shownColumns = this._tableVizData.getColumns().getNames();
        const filter = this._tableVizData.getFilterCriteria();
        const data = {
            checkboxes: defaultColumns.map(columnObj=>{return{name: columnObj.getDisplayName(),
                checked: shownColumns.includes(columnObj.getDisplayName()), title: columnObj.getDisplayOptions().title, customProp: columnObj.getDisplayOptions().customProp}}),
            rowsFilter: filter ? filter.getFilterString() : ''
        }
        const initiallyChecked = data.checkboxes.filter(checkbox=>checkbox.checked);
        let dialog = new ColumnPickerDialog().render(data);
        dialog.setActiveTab(activeTab);
        dialog.onOk(async ({columnNames, filterString}) => {
            if (filterString.length > 0)
                try {
                    TableColumn.assertExpressionDoesNotThrows(filterString);
                } catch (err) {
                    dialog.showRowFilterError(err.message)
                    return;
                }
            if (columnNames.length === 0 && shownColumns.length === initiallyChecked.length) {
                const notificationDialog = new SimpleNotificationDialog();
                notificationDialog.render('No columns selected, select at least one column to show.', 'last-table-column-delete-warning');
                notificationDialog.onOk(()=>dialog.setActiveTab(0));
                return;
            }
            const toAdd = columnNames.filter(columnName=>!shownColumns.includes(columnName));
            toAdd.forEach(columnName=>{
                const tc = this._defaultColumns.findByName(columnName);
                this.addColumn(new TableColumn(tc.getDisplayName(), tc.getExpression(), tc.getDisplayOptions()))
            });
            const toDelete = initiallyChecked.filter(checkbox=>!columnNames.includes(checkbox.name)).map(checkbox=>checkbox.name);
            toDelete.forEach(columnName=>{
                this.removeColumn(columnName)
            });
            if (filterString !== data.rowsFilter) {
                const filterCriteria = filterString.length > 0 ? new FilterCriteria(filterString) : null;
                this._tableVizData.setFilter(filterCriteria);
                this.chart.setRowsFilter(filterCriteria);
                this._props.table.rowFilter = filterString;
                await this.chart.draw();
            }
            dialog.remove();
            await this._visGrid.saveProperties();
        })
    }

    showColumnPropertiesDialog(tc, index) {
        let dialog = new EditColumnDialog().render(tc);
        dialog.on('ok', async (formData) => {
            try {
                const displayOpts = Object.assign({}, tc.getDisplayOptions());
                displayOpts.name = formData.name;
                displayOpts.expression = formData.expression;
                TableColumn.assertExpressionDoesNotThrows(displayOpts.expression);
                displayOpts.autoDetectType = false;
                displayOpts.type = formData.type;
                index === -1
                    ? this.addColumn(new TableColumn(tc.getDisplayName(), formData.expression, displayOpts))
                    : this.updateColumn(index, displayOpts);
                await this._visGrid.saveProperties();
                dialog.remove();
            } catch (err) {
                if (err instanceof OperationNotAllowedError || err instanceof HttpError) {
                    dialog.remove();
                    throw err;
                }
                let field = 'expression';
                if (err instanceof ColumnValidationError)
                    field = err.field;
                dialog.showError(field, err.message)
            }
        })
        dialog.on('delete', async (data) => {
            try {
                this.removeColumn(data.getDisplayName());
                await this._visGrid.saveProperties();
                dialog.remove();
            } catch (err) {
                if (err instanceof OperationNotAllowedError || err instanceof HttpError) {
                    dialog.remove();
                    throw err;
                } else
                    new SimpleNotificationDialog().render(err.message, 'last-table-column-delete-warning')
            }
        })
    }

    markVisualizationAsNew() {
        this._root.classList.add('visualization-component--new');
        this.#removeNewVisualizationClass = this.#removeNewVisualizationClassFunction.bind(this)
        this.#onMouseLeave = this.#onMouseLeaveFunction.bind(this);

        this._root.addEventListener('mouseenter', this.#removeNewVisualizationClass);
        this._root.addEventListener('mouseleave', this.#onMouseLeave);
    }

    switchView(viewName) {
        const tableTab = this._root.querySelector('.table-tab');
        const chartTab = this._root.querySelector('.chart-tab');
        this._root.querySelectorAll('#toggle-view svg').forEach(btn=>{btn.style.display = 'none'})
        if (viewName === 'chart') {
            this._props.card.activeView = 'chart';
            tableTab.style.display = 'none';
            chartTab.style.display = 'block';
            this._root.querySelector('#toggle-view #table-icon').style.display = null;
            this.chart.draw();
        } else {
            this._props.card.activeView = 'table';
            tableTab.style.display = 'block';
            chartTab.style.display = 'none';
            this._root.querySelector('#toggle-view #chart-icon').style.display = null;
        }
    }

    #showAxesChoiceDialog(){
        const columns = this._tableVizData.getColumns();
        let axisX = this.chart.axisX, axisY = this.chart.axisY;
        const missingAxes = [];
        if(!columns.hasName(axisX.getName()))
            missingAxes.push(axisX);
        if(!columns.hasName(axisY.getName()))
            missingAxes.push(axisY);
        this.chart.openAxisPropertiesDialog(missingAxes);
    }

    #removeNewVisualizationClassFunction(){
        this.#timer = setTimeout(() => {
            this._root.classList.remove('visualization-component--new');
            this._root.removeEventListener('mouseenter', this.#removeNewVisualizationClass);
            this._root.removeEventListener('mouseleave', this.#onMouseLeave);
        }, 1000)
    }

    #onMouseLeaveFunction(){
        clearTimeout(this.#timer)
    }

    /**
     * @param {VisualizationProperties} props
     * @param {InjectionRow[] | PeakRow[]} rows
     * @param {TableColumns} defaultColumns
     * @param {VisualizationDataSelection} selection
     * @param tableDataConverters
     * @param {VisualizationGrid} visGrid
     * @param {RegressionApiClient} regressionApiClient
     * @returns {Promise<VisualisationComponent>}
     */
    static async createFromProperties(props, rows, defaultColumns, selection,
                                      tableDataConverters, visGrid, regressionApiClient){
        const cols = new TableColumns(props.table.columns.map(c=> TableColumn.parseServerJson(c)));
        let dataTable = new DataTable(rows, cols);
        let component = new VisualisationComponent(dataTable, selection, defaultColumns, props,
            tableDataConverters, visGrid, regressionApiClient);
        for (const line of props.chart.lines)
            await component.chart.statisticComponents.create(LineConfig.parseServerJson(line), regressionApiClient)
        await component.switchView(props.card.activeView);
        return component;
    }

    _render(name) {
        let template = document.createElement('template');
        template.innerHTML = `
              <div class="visualisation-component" style="width: auto">              
                <div class="move-bar"></div>
                <div class="visualisation-component-header">
                  <div class="vis-name">
                    <div style="width: fit-content; max-width: calc(100% - 20px); display: inline-block">
                      <edit-text value="${safeText(name)}" maxLength="500" minLength="1" required></edit-text>
                    </div>
                  </div>
                  <div class="vis-buttons">
                    <div id="toggle-view">
                      <svg xmlns="http://www.w3.org/2000/svg" id="table-icon" height="18px" viewBox="0 0 24 24" width="18px" fill="#000000">
                        <path d="M20 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 2v3H5V5h15zm-5 14h-5v-9h5v9zM5 10h3v9H5v-9zm12 9v-9h3v9h-3z"/>
                      </svg>
                      <svg xmlns="http://www.w3.org/2000/svg" id="chart-icon" height="18px" viewBox="0 0 24 24" width="18px" fill="#000000" style="display: none">
                        <path d="M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10zm2.03 0v8.99H22c-.47-4.74-4.24-8.52-8.97-8.99zm0 11.01V22c4.74-.47 8.5-4.25 8.97-8.99h-8.97z"/>
                      </svg>
                    </div>
                    <div> 
                      <svg xmlns="http://www.w3.org/2000/svg" id="vis-more-icon" height="18px" viewBox="-2 -2 22 22" width="18px" fill="#000000">
                        <path d="M8 12a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-6a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-6a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"></path>
                      </svg>
					  <div class="md-list-wrapper dropdown-menu vis-options-dropdown"> 
                        <ul class="md-list">
                          <li class="md-list-item" data-purpose="select columns"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">add_column_right</span>
                              <span class="md-list-item__text">Add column</span> 
                            </div>
                          </li>
                          <li class="md-list-item" data-purpose="add calculated"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">add_column_right</span>
                              <span class="md-list-item__text">Add calculated column</span> 
                            </div>
                          </li>
                          <li class="md-list-item" data-purpose="filter rows"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">filter_alt</span>
                              <span class="md-list-item__text">Filter rows</span> 
                            </div>
                          </li>
                          <li class="md-list-item item-separator">
                            <span class="separator-line"></span>
                          </li>
                          <li class="md-list-item" data-purpose="show options"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">bar_chart_4_bars</span>
                              <span class="md-list-item__text">Show chart statistics</span> 
                            </div>
                          </li>
                          <li class="md-list-item item-separator">
                            <span class="separator-line"></span>
                          </li>
                          <li class="md-list-item" data-purpose="export csv"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">csv</span>
                              <span class="md-list-item__text">Export to CSV</span> 
                            </div>
                          </li>
                          <li class="md-list-item" data-purpose="duplicate"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">content_copy</span>
                              <span class="md-list-item__text">Duplicate</span> 
                            </div>
                          </li>
                          <li class="md-list-item" data-purpose="delete"> 
                            <div class="md-list-item-content"> 
                              <span class="material-symbols-outlined md-list-item__icon" style="width: 1rem">delete</span>
                              <span class="md-list-item__text">Delete</span> 
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="visualisation-component-body">
                  <div class="table-tab"></div>
                  <div class="chart-tab" style="display: none"></div>
                </div>
              </div>`;
        document.body.appendChild(template.content.cloneNode(true))
        return document.body.lastChild;
    }
}

export class DraggableVisualisation {
    constructor(vis, grid) {
        this._grid = grid;
        this.rectangle = {
            mouseOffsetX: 0,
            mouseOffsetY: 0
        }
        this.placeholder = {
            element: null,
            leftSibling: null,
            rightSibling: null,
            row: null
        }
        this.siblings = {
            row: null,
            visLeft: null,
            visRight: null,
            rowAbove: null,
            rowBelow: null
        }
        this._fn = {
            mouseDown: this.#onMouseDown.bind(this),
            mouseMove: this.#onMouseMove.bind(this, vis),
            mouseUp: this.#onMouseUp.bind(this, vis),

        }
        let moveBar = vis.querySelector('.move-bar');
        moveBar.addEventListener('mousedown', this._fn.mouseDown);
    }

    getSiblings(mouseX, mouseY) {
        let res = {
            rowAbove: null,
            row: null,
            rowBelow: null,
            visLeft: null,
            visRight: null,
            rowAboveIndex: null,
            rowBelowIndex: null,
            columnLeftIndex: null,
            columnRightIndex: null
        };

        const grid = document.querySelector('.vis-grid');
        const rows = [...grid.querySelectorAll('.vis-grid-row')];

        if (!grid || rows.length === 0) return res; // If the grid or rows are missing

        // Get the coordinates of an element
        const getRect = el => el.getBoundingClientRect();

        // Find the row where the mouse is located
        let rowIndex = rows.findIndex(row => {
            const rect = getRect(row);
            return mouseY >= rect.top && mouseY <= rect.bottom;
        });

        if (rowIndex === -1) {
            // Logic for the mouse being between rows
            for (let i = 0; i < rows.length - 1; i++) {
                const currentRect = getRect(rows[i]);
                const nextRect = getRect(rows[i + 1]);

                if (mouseY > currentRect.bottom && mouseY < nextRect.top) {
                    res.rowAbove = rows[i];
                    res.rowAboveIndex = i;
                    res.rowBelow = rows[i + 1];
                    res.rowBelowIndex = i + 1;
                    return res;
                }
            }

            // If the mouse is above the first row
            if (mouseY < getRect(rows[0]).top) {
                res.rowBelow = rows[0];
                res.rowBelowIndex = 0;
                return res;
            }

            // If the mouse is below the last row
            if (mouseY > getRect(rows[rows.length - 1]).bottom) {
                res.rowAbove = rows[rows.length - 1];
                res.rowAboveIndex = rows.length - 1;
                return res;
            }
        } else {
            const rowRect = getRect(rows[rowIndex]);
            const verticalPosition = (mouseY - rowRect.top) / rowRect.height;

            if (verticalPosition <= 0.15) {
                // Mouse is in the upper part of the row
                res.rowAbove = rows[rowIndex - 1] || null;
                res.rowAboveIndex = rowIndex - 1 >= 0 ? rowIndex - 1 : null;
                res.rowBelow = rows[rowIndex];
                res.rowBelowIndex = rowIndex;
                return res;
            } else if (verticalPosition >= 0.85) {
                // Mouse is in the lower part of the row
                res.rowAbove = rows[rowIndex];
                res.rowAboveIndex = rowIndex;
                res.rowBelow = rows[rowIndex + 1] || null;
                res.rowBelowIndex = rowIndex + 1 < rows.length ? rowIndex + 1 : null;
                return res;
            }

            // Logic for the middle part of the row
            res.row = rows[rowIndex];
            res.rowAbove = null;
            res.rowAboveIndex = null;
            res.rowBelow = null;
            res.rowBelowIndex = null;

            const visArr = [...rows[rowIndex].querySelectorAll('.visualisation-component')];

            // Logic for finding a card
            let visIndex = visArr.findIndex(vis => {
                const rect = getRect(vis);
                return mouseX >= rect.left && mouseX <= rect.right;
            });

            if (visIndex !== -1) {
                // Mouse is over a card
                const visRect = getRect(visArr[visIndex]);
                if (mouseX < visRect.left + visRect.width / 2) {
                    res.visLeft = visIndex > 0 ? visArr[visIndex - 1] : null;
                    res.visRight = visArr[visIndex];
                } else {
                    res.visLeft = visArr[visIndex];
                    res.visRight = visIndex < visArr.length - 1 ? visArr[visIndex + 1] : null;
                }
            } else {
                // Mouse is outside the cards
                if (mouseX < getRect(visArr[0]).left) {
                    // To the left of all cards
                    res.visLeft = null;
                    res.visRight = visArr[0];
                } else if (mouseX > getRect(visArr[visArr.length - 1]).right) {
                    // To the right of all cards
                    res.visLeft = visArr[visArr.length - 1];
                    res.visRight = null;
                } else {
                    // Mouse is between cards
                    for (let i = 0; i < visArr.length - 1; i++) {
                        const current = getRect(visArr[i]);
                        const next = getRect(visArr[i + 1]);

                        if (mouseX > current.right && mouseX < next.left) {
                            res.visLeft = visArr[i];
                            res.visRight = visArr[i + 1];
                            break;
                        }
                    }
                }
            }
        }
        return res;
    }

    #createRectangle() {
        this.rectangle = document.createElement('div');
        this.rectangle.style.position = 'absolute';
        this.rectangle.style.border = '2px solid black';
        this.rectangle.style.width = '25%';
        this.rectangle.style.height = '512px';
        this.rectangle.style.zIndex = '1000';
        document.body.appendChild(this.rectangle);
    }

    #updateRectanglePosition(x, y) {
        if (this.rectangle) {
            this.rectangle.style.left = `${x - this.rectangle.offsetWidth / 2}px`;
            this.rectangle.style.top = `${y}px`;
        }
    }

    #onMouseMove(visEl, evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.#scrollWindow(evt.clientX, evt.clientY, evt.movementX, evt.movementY);
        if (this.rectangle) {
            this.#updateRectanglePosition(evt.pageX, evt.pageY);
        }
        this.siblings = this.getSiblings(evt.clientX, evt.clientY);
        let {row, rowAbove, rowBelow, visLeft, visRight} = this.siblings;
        this.#removeClassesAndRows();
        if (row) {
            if (row.querySelectorAll('.visualisation-component').length < DraggableVisualisation.maxCardNumber || row.contains(visEl)) {
                if (visLeft)
                    visLeft.classList.add('visualisation-component__right-shadow');
                if (visRight)
                    visRight.classList.add('visualisation-component__left-shadow')
            } else {
                row.classList.add('vis-grid-row--forbidden')
            }
        } else {
            if (rowBelow) {
                rowBelow.classList.add('vis-grid-row__top-shadow');
            }
            if (rowAbove) {
                rowAbove.classList.add('vis-grid-row__bottom-shadow');
            }
        }
    }

    #removeClassesAndRows(){
        const {visLeft, visRight, rowBelow, rowAbove, row} = this.siblings;
        document.querySelectorAll('.visualisation-component__right-shadow')
            .forEach((el)=>{
                if (el !== visLeft)
                    el.classList.remove('visualisation-component__right-shadow')} )
        document.querySelectorAll('.visualisation-component__left-shadow')
            .forEach((el) => {
                if (el !== visRight)
                    el.classList.remove('visualisation-component__left-shadow')
            } )
        document.querySelectorAll('.vis-grid-row__top-shadow')
            .forEach((el) => {
                if (el !== rowBelow)
                    el.classList.remove('vis-grid-row__top-shadow')
            } )
        document.querySelectorAll('.vis-grid-row__bottom-shadow')
            .forEach((el) => {
                if (el !== rowAbove)
                    el.classList.remove('vis-grid-row__bottom-shadow')
            } )
        document.querySelectorAll('.vis-grid-row--forbidden')
            .forEach((el) => {
                if (el !== row)
                    el.classList.remove('vis-grid-row--forbidden')
            } )
        const rows = document.querySelectorAll('.vis-grid-row');
        rows.forEach((row)=>{
            if (!row.hasChildNodes()){
                row.remove()
            }
        })

    }
    #onMouseDown(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (evt.button !== 0)
            return;
        this.rectangle.mouseOffsetX = evt.offsetX;
        this.rectangle.mouseOffsetY = evt.offsetY;
        this.#createRectangle();
        this.#updateRectanglePosition(evt.pageX, evt.pageY);
        window.addEventListener('mousemove', this._fn.mouseMove);
        window.addEventListener('mouseup', this._fn.mouseUp);
    }

    /**
     * @param {HTMLElement} vis
     * @returns {Promise<void>}
     */
    async #onMouseUp(vis) {
        if (this.rectangle) {
            this.rectangle.remove();
        }
        window.removeEventListener('mousemove', this._fn.mouseMove);
        window.removeEventListener('mouseup', this._fn.mouseUp);
        await this.#redrawGridAndSave(vis);
        this.siblings = {}
        this.#removeClassesAndRows();
    }

    /**
     * @param {HTMLElement} vis
     * @returns {Promise<void>}
     */
    async #redrawGridAndSave(vis){
        const hasNonNullProperty = Object.values(this.siblings).some(value => value !== null)
        if (!hasNonNullProperty)
            return
        let {row, rowAbove, rowBelow, visLeft, visRight} = this.siblings;

        if (row) {
            if (row.querySelectorAll('.visualisation-component').length >= DraggableVisualisation.maxCardNumber && !row.contains(vis)){
                return
            }
            if (visLeft){
                visLeft.after(vis)
            } else if (visRight) {
                visRight.before(vis)
            }
        } else {
            const newRow = document.createElement('div');
            newRow.classList.add('vis-grid-row');
            newRow.insertAdjacentElement("beforeend", vis)
            if (rowAbove){
                rowAbove.after(newRow)
            } else if (rowBelow){
                rowBelow.before(newRow)
            }
        }
        this.siblings = {};
        this.#removeClassesAndRows();
        this._grid._updateAllLocations();
        await this._grid.saveProperties();
    }

    #scrollWindow(mouseX, mouseY, movementX, movementY) {
        let x = 0, y = 0;
        if (mouseX < 50 && movementX <= 0)
            x = -10;
        if (mouseX + 50 > window.innerWidth && movementX >= 0)
            x = 10;
        if (mouseY < 50 && movementY <= 0)
            y = -10;
        if (mouseY + 50 > window.innerHeight && movementY >= 0)
            y = 10;
        window.scrollBy(x, y)
    }

    static maxCardNumber = 4;
}